/**
 * @file: useAiCreditBalance.ts
 * @copyright: (c) 2019-2026 sichuan zhichetech co., ltd.
 */

import { AiCreditStoreBalanceView } from '@euler/model/AiCreditStoreBalanceView';
import { useServiceFactory } from '@euler/services/factory';
import { useLocalObservable } from '@euler/utils/hooks';
import { catchError, defer, map, of } from 'rxjs';

/**
 * The store's AI credits balance, fetched each time the panel opens (the
 * panel content mounts on open). Null until loaded, when the store has no
 * balance yet, or when the fetch fails — the panel then shows nothing.
 * With `enabled` false it fetches nothing and stays null.
 */
export const useAiCreditBalance = (
  enabled = true,
): AiCreditStoreBalanceView | null => {
  const { aiCreditService } = useServiceFactory();
  return useLocalObservable(
    () =>
      enabled
        ? defer(() => aiCreditService.getStoreBalance()).pipe(
            map(view => (view.exists ? view : null)),
            catchError(e => {
              console.error('[ai] failed to load ai credits balance:', e);
              return of(null);
            }),
          )
        : of(null),
    null,
    [aiCreditService, enabled],
  );
};
